import { useState, useEffect } from 'react';
import { dailyKey, dailyNumber, MODES, MODE_IDS } from '@wot/shared';
import type { ModeId } from '@wot/shared';
import { MODE_STYLE } from '../lib/modeStyle';
import { useGameStore } from '../store/gameStore';
import { useAuth } from '../hooks/useAuth';
import { haptic, unlockAudio } from '../lib/sfx';
import { getGuestName } from '../lib/guestName';
import { IS_EMBED, leaveToFullApp } from '../lib/embed';
import { supabase } from '../supabase';
import { useUpdateCheck } from '../hooks/useUpdateCheck';

type FeedItem = { nickname: string; score: number; realm: string };

// Dev-only preview of the signed-in Home without a Google round trip:
// open /?as=user once, the flag sticks for the tab. Compiled out of prod.
const DEV_AS_USER = (() => {
  if (!import.meta.env.DEV) return false;
  try {
    if (new URLSearchParams(window.location.search).get('as') === 'user') sessionStorage.setItem('wot_dev_as', 'user');
    if (new URLSearchParams(window.location.search).get('as') === 'guest') sessionStorage.removeItem('wot_dev_as');
    return sessionStorage.getItem('wot_dev_as') === 'user';
  } catch { return false; }
})();

export function Home() {
  const [nickname, setNickname] = useState('');
  const [joinCode, setJoinCode] = useState('');
  const [showJoinInput, setShowJoinInput] = useState(false);

  const auth = useAuth();
  const { loading: authLoading, signInWithGoogle, signOut } = auth;
  const user = DEV_AS_USER ? (auth.user ?? ({ id: 'dev' } as any)) : auth.user;
  const displayName = DEV_AS_USER && !auth.user ? 'Kemal' : auth.displayName;
  const avatarUrl = DEV_AS_USER && !auth.user ? null : auth.avatarUrl;
  const { update, dismiss, openStore } = useUpdateCheck();

  // Auto-set nickname from Google profile
  useEffect(() => {
    if (user && displayName && !nickname) {
      const name = displayName.split(' ')[0].toUpperCase().slice(0, 16);
      setNickname(name);
      setNick(name);
    }
  }, [user, displayName]);

  const setView = useGameStore((s) => s.setView);
  const setNick = useGameStore((s) => s.setNickname);
  const setGameMode = useGameStore((s) => s.setGameMode);
  const startDailyMatch = useGameStore((s) => s.startDailyMatch);

  // Live feed — real top scores from the leaderboard. Deduped by player.
  // Hidden entirely below MIN_TICKER rows so we never fabricate social proof.
  const MIN_TICKER = 6;
  const [liveFeed, setLiveFeed] = useState<FeedItem[]>([]);
  useEffect(() => {
    let active = true;
    (async () => {
      const { data, error } = await supabase
        .from('leaderboard')
        .select('nickname, total_score, realm')
        .order('total_score', { ascending: false })
        .limit(30);
      if (!active || error || !data) return;
      const seen = new Set<string>();
      const feed: FeedItem[] = [];
      for (const row of data as any[]) {
        const key = (row.nickname || '').toUpperCase();
        if (!key || seen.has(key)) continue;
        seen.add(key);
        feed.push({ nickname: row.nickname, score: row.total_score, realm: row.realm });
        if (feed.length >= 12) break;
      }
      setLiveFeed(feed);
    })();
    return () => { active = false; };
  }, []);

  // Multiplayer (Quick Match, Create Room, Join) requires a signed-in account.
  // Solo Play is open to everyone; guests play under a default name.
  const canMultiplayer = !!user;

  async function handleQuickMatch() {
    // A guest who taps a match card is asking to play, so offer the door
    // instead of a dead button. In the embed the door is the real site: an
    // iframe on a foreign origin cannot finish a Google sign-in.
    if (!canMultiplayer) { IS_EMBED ? leaveToFullApp() : void signInWithGoogle(); return; }
    haptic('medium');
    setNick(nickname.trim());
    setGameMode('quick_match');
    await useGameStore.getState().quickMatch();
  }

  // PLAY is one tap to a question: Rush, every realm, no setup screen.
  // The chips under it open the setup with the mode already chosen.
  async function handlePlay() {
    haptic('medium');
    setNick(nickname.trim() || getGuestName());
    setGameMode('solo');
    const st = useGameStore.getState();
    st.setSelectedMode('RUSH');
    st.setSelectedRealm('ALL');
    await st.startSoloMatch('ALL');
  }

  function handleSetup(mode?: ModeId) {
    haptic('light');
    setNick(nickname.trim() || getGuestName());
    setGameMode('solo');
    if (mode) useGameStore.getState().setSelectedMode(mode);
    setView('config');
  }

  async function handleCreateRoom() {
    if (!canMultiplayer) { IS_EMBED ? leaveToFullApp() : void signInWithGoogle(); return; }
    haptic('medium');
    setNick(nickname.trim());
    setGameMode('create_room');
    setView('config');
  }

  async function handleJoinRoom() {
    if (joinCode.trim().length < 4) return;
    setNick(nickname.trim() || getGuestName());
    setGameMode('create_room');
    await useGameStore.getState().joinRoom(joinCode.trim(), 'code');
  }

  // A build the backend no longer supports gets a wall, not a card. Anything
  // it could do while this old would write through retired code paths.
  if (update.status === 'required') {
    return (
      <div className="page page-home" style={{ justifyContent: 'center', gap: 18 }}>
        <h1 className="wot-logo">
          <span className="wot-letter">W</span>
          <span className="wot-letter">O</span>
          <span className="wot-letter">T</span>
        </h1>
        <div style={{ textAlign: 'center', maxWidth: 300, margin: '0 auto' }}>
          <div style={{ fontSize: '0.85rem', fontWeight: 700, letterSpacing: '0.12em', color: '#e8e6f0', marginBottom: 8 }}>
            UPDATE REQUIRED
          </div>
          <div style={{ fontSize: '0.9rem', color: '#9a96ac', lineHeight: 1.5 }}>
            This version is no longer supported. Get the latest from Google Play to keep playing.
          </div>
        </div>
        <button
          onClick={openStore}
          style={{
            minHeight: 48, padding: '0 28px', borderRadius: 10, cursor: 'pointer',
            background: 'rgba(20, 184, 166, 0.12)', border: '1px solid #14B8A6',
            color: '#14B8A6', fontSize: '0.8rem', fontWeight: 700, letterSpacing: '0.1em',
          }}
        >
          UPDATE ON GOOGLE PLAY
        </button>
      </div>
    );
  }

  return (
    <div className="page page-home" onClick={unlockAudio}>

      {/* ── LOGO ── */}
      <h1 className="wot-logo">
        <span className="wot-letter">W</span>
        <span className="wot-letter">O</span>
        <span className="wot-letter">T</span>
      </h1>
      <span className="wot-subtitle">WORLD OF TRIVIA</span>

      {/* ── USER PROFILE / LOGIN ── */}
      <div className="home-setup">
        {user ? (
          <div className="home-user-profile">
            {avatarUrl && <img src={avatarUrl} alt="" className="user-avatar" referrerPolicy="no-referrer" />}
            <span className="user-display-name">{displayName.split(' ')[0].toUpperCase()}</span>
            <button className="sign-out-btn" onClick={signOut} title="Sign Out">
              <svg width="14" height="14" viewBox="0 0 24 24" fill="currentColor">
                <path d="M17 7l-1.41 1.41L18.17 11H8v2h10.17l-2.58 2.59L17 17l5-5zM4 5h8V3H4c-1.1 0-2 .9-2 2v14c0 1.1.9 2 2 2h8v-2H4V5z"/>
              </svg>
            </button>
          </div>
        ) : (
          <button className="google-sign-in-btn" onClick={IS_EMBED ? leaveToFullApp : signInWithGoogle} disabled={authLoading}>
            <svg className="google-icon" viewBox="0 0 24 24" width="18" height="18">
              <path d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92a5.06 5.06 0 01-2.2 3.32v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.1z" fill="#4285F4"/>
              <path d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z" fill="#34A853"/>
              <path d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z" fill="#FBBC05"/>
              <path d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z" fill="#EA4335"/>
            </svg>
            {authLoading ? 'LOADING...' : IS_EMBED ? 'SIGN IN ON THE FULL SITE' : 'SIGN IN WITH GOOGLE'}
          </button>
        )}
      </div>

      {/* ── LIVE FEED TICKER ── */}
      {liveFeed.length >= MIN_TICKER && (
        <div className="live-ticker">
          <div className="live-ticker-track">
            {[...liveFeed, ...liveFeed].map((item, i) => (
              <span className="live-ticker-item" key={i}>
                <strong>{item.nickname}</strong> {(item.score / 1000).toFixed(1)}K · <span className="ticker-realm">{item.realm}</span>
              </span>
            ))}
          </div>
        </div>
      )}


      {/* ── UPDATE NOTICE ── */}
      {update.status === 'available' && (
        <div style={{ width: '100%', maxWidth: 400, margin: '0 auto 10px', padding: '0 16px', boxSizing: 'border-box' }}>
          <div style={{
            display: 'flex', alignItems: 'center', gap: 10, minHeight: 46,
            padding: '8px 12px', borderRadius: 10,
            background: 'rgba(62, 123, 250, 0.08)', border: '1px solid rgba(62, 123, 250, 0.45)',
          }}>
            <span style={{ flex: 1, fontSize: '0.875rem', color: '#D5D1E4', letterSpacing: '0.01em' }}>
              A new version is on Google Play.
            </span>
            <button
              onClick={openStore}
              style={{
                minHeight: 32, padding: '0 12px', borderRadius: 8, cursor: 'pointer',
                background: 'transparent', border: '1px solid #3E7BFA', color: '#7EA8FF',
                fontSize: '0.85rem', fontWeight: 700, letterSpacing: '0.06em',
              }}
            >
              UPDATE
            </button>
            <button
              onClick={dismiss}
              aria-label="Dismiss"
              style={{
                minHeight: 32, minWidth: 32, borderRadius: 8, cursor: 'pointer',
                background: 'transparent', border: 'none', color: '#6f6a84', fontSize: '1rem',
              }}
            >
              &times;
            </button>
          </div>
        </div>
      )}

      {/* ── THE DAILY ──
          One shared set a day, everyone on the planet gets the same ten.
          Played state and streak live in localStorage; the card flips to its
          result and waits for the next UTC midnight. */}
      <div style={{ width: '100%', maxWidth: 480, margin: '0 auto 10px', boxSizing: 'border-box' }}>
        {(() => {
          const today = dailyKey();
          let daily: { key: string; score: number; correct: number; total: number; streak: number } | null = null;
          try { daily = JSON.parse(localStorage.getItem('wot_daily_v1') || 'null'); } catch { /* ignore */ }
          const playedToday = daily?.key === today;
          return (
            <button
              onClick={() => { if (!playedToday) { haptic('medium'); startDailyMatch(); } }}
              disabled={playedToday}
              style={{
                width: '100%', minHeight: 76, borderRadius: 12,
               
                background: playedToday
                  ? 'linear-gradient(115deg, rgba(20,184,166,0.06) 0%, transparent 55%), #16223A'
                  : 'linear-gradient(115deg, rgba(20,184,166,0.18) 0%, transparent 60%), #1C2A45',
                border: `2px solid ${playedToday ? 'rgba(255,255,255,0.12)' : '#14B8A6'}`,
                borderLeft: `4px solid ${playedToday ? 'rgba(45,212,191,0.4)' : '#2DD4BF'}`,
                boxShadow: playedToday
                  ? '0 5px 0 #0D1626'
                  : '0 5px 0 #0E7A6E, 0 0 16px rgba(20,184,166,0.22)',
                display: 'flex', alignItems: 'center', justifyContent: 'space-between',
                padding: '12px 16px', cursor: playedToday ? 'default' : 'pointer',
                transition: 'all 0.15s ease',
              }}
            >
              <span style={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-start', gap: 3, minWidth: 0, flex: 1, marginRight: 10 }}>
                <span style={{
                  fontFamily: 'var(--font-heading)', fontWeight: 900, fontStyle: 'italic',
                  fontSize: 'clamp(0.95rem, 4.6vw, 1.05rem)', letterSpacing: '0.04em', lineHeight: 1.15,
                  color: playedToday ? '#ccccd4' : '#2DD4BF', maxWidth: '100%',
                }}>
                  THE DAILY #{dailyNumber()}
                </span>
                <span style={{ fontFamily: 'var(--font-sans)', fontSize: '0.85rem', letterSpacing: '0.01em', lineHeight: 1.25, color: '#C8C8D6', maxWidth: '100%' }}>
                  {playedToday
                    ? `${daily!.correct}/${daily!.total} today. New set at midnight.`
                    : 'Everyone gets the same ten.'}
                </span>
              </span>
              <span style={{
                fontFamily: 'var(--font-sans)', fontSize: '0.85rem', fontWeight: 800,
                letterSpacing: '0.08em', color: playedToday ? '#9090A2' : '#0A1222',
                background: playedToday ? 'transparent' : '#2DD4BF',
                padding: playedToday ? 0 : '8px 14px', borderRadius: 999, flexShrink: 0,
               
              }}>
                {playedToday
                  ? (daily!.streak > 1 ? `STREAK ${daily!.streak}` : 'DONE')
                  : 'PLAY'}
              </span>
            </button>
          );
        })()}
      </div>

      {/* ── PLAY: the one hero, the same for guest and account.
          One tap lands on a question; the chips open the setup. ── */}
      <div className="home-actions">
        <button
          className="action-card primary"
          style={{ '--accent-color': '#14B8A6' } as React.CSSProperties}
          onClick={handlePlay}
          id="btn-solo-play"
        >
          <span className="action-card-text">
            <span className="action-card-label">SOLO PLAY</span>
            <span className="action-card-sub">7 quick questions. All realms.</span>
          </span>
          <svg className="action-card-icon" viewBox="0 0 24 24" fill="currentColor">
            <path d="M8 5v14l11-7z"/>
          </svg>
        </button>

        <div className="home-chips" role="group" aria-label="Solo modes">
          {MODE_IDS.map((id) => {
            const M = MODE_STYLE[id];
            return (
              <button
                key={id}
                className="home-chip home-chip-mode"
                style={{ '--mode-color': M.color, '--mode-bright': M.bright } as React.CSSProperties}
                onClick={() => handleSetup(id)}
                id={`btn-mode-${id.toLowerCase()}`}
                title={M.line}
              >
                <svg viewBox="0 0 24 24" width="18" height="18" fill="currentColor" aria-hidden="true"><path d={M.icon} /></svg>
                {MODES[id].name.toUpperCase()}
              </button>
            );
          })}
        </div>
        <button className="home-chip home-chip-wide" onClick={() => handleSetup()} id="btn-pick-realm">PICK A REALM</button>

        {/* ── 1v1: two full cards, never greyed out. A guest tap opens
            Google sign-in; the sub-line says so before the tap. ── */}
        {/* The banner also holds the room code: a guest with an invite
            needs nothing else on this screen. */}
        <div className="home-banner">
          <div className="home-banner-row">
            <span className="home-banner-title">MULTIPLAYER <em>1 VS 1</em></span>
            <button
              className={`home-banner-code${showJoinInput ? ' open' : ''}`}
              onClick={() => setShowJoinInput((v) => !v)}
              id="btn-show-join"
            >
              ROOM CODE
            </button>
          </div>
          {showJoinInput && (
            <div className="home-join-row animate-in">
              <input
                className="input input-code"
                type="text"
                placeholder="CODE"
                value={joinCode}
                onChange={(e) => setJoinCode(e.target.value.toUpperCase().slice(0, 6))}
                onKeyDown={(e) => { if (e.key === 'Enter') void handleJoinRoom(); }}
                autoFocus
                id="input-join-code"
              />
              <button
                className="btn btn-primary"
                onClick={handleJoinRoom}
                disabled={joinCode.trim().length < 4}
                id="btn-join-room"
              >
                JOIN
              </button>
            </div>
          )}
        </div>

        <button
          className="action-card"
          style={{ '--accent-color': '#3E7BFA' } as React.CSSProperties}
          onClick={handleQuickMatch}
          id="btn-quick-match"
        >
          <span className="action-card-text">
            <span className="action-card-label">QUICK MATCH</span>
            <span className="action-card-sub">{canMultiplayer ? 'Anyone online, right now.' : 'Sign in with Google to start one.'}</span>
          </span>
          <svg className="action-card-icon" viewBox="0 0 24 24" fill="currentColor">
            <path d="M11 21h-1l1-7H7.5c-.88 0-.33-.75-.31-.78C8.48 10.94 10.42 7.54 13.01 3h1l-1 7h3.51c.4 0 .62.19.4.66C12.97 17.55 11 21 11 21z"/>
          </svg>
        </button>

        <button
          className="action-card"
          style={{ '--accent-color': '#F43F5E' } as React.CSSProperties}
          onClick={handleCreateRoom}
          id="btn-create-room"
        >
          <span className="action-card-text">
            <span className="action-card-label">CREATE ROOM</span>
            <span className="action-card-sub">{canMultiplayer ? 'Friends, by invite link.' : 'Sign in with Google to start one.'}</span>
          </span>
          <svg className="action-card-icon" viewBox="0 0 24 24" fill="currentColor">
            <path d="M16 11c1.66 0 2.99-1.34 2.99-3S17.66 5 16 5c-1.66 0-3 1.34-3 3s1.34 3 3 3zm-8 0c1.66 0 2.99-1.34 2.99-3S9.66 5 8 5C6.34 5 5 6.34 5 8s1.34 3 3 3zm0 2c-2.33 0-7 1.17-7 3.5V19h14v-2.5c0-2.33-4.67-3.5-7-3.5zm8 0c-.29 0-.62.02-.97.05 1.16.84 1.97 1.97 1.97 3.45V19h6v-2.5c0-2.33-4.67-3.5-7-3.5z"/>
          </svg>
        </button>
      </div>




    </div>
  );
}
